import React, { useContext, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { motion } from "framer-motion";

import {
    Box,
    Typography,
    Paper,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    CircularProgress,
    useMediaQuery,
} from "@mui/material";

import {
    Code,
    Email,
    Lock,
    Visibility,
    VisibilityOff,
    ArrowBack,
    Security,
    AutoAwesome,
    Bolt,
    VerifiedUser,
    Storage,
    Lan,
    Hub,
    Terminal,
} from "@mui/icons-material";

import api from "../api/api";
import { AuthContext } from "../AuthContext";

import student from "./schoolmanagementsystem.png";

const COLORS = {
    black: "#080808",
    dark: "#0D1117",
    darkBlue: "#071426",
    deepBlue: "#0B1F3A",
    navy: "#06111F",
    blue: "#087EA4",
    oceanBlue: "#0A8FB8",
    cyan: "#27C7F5",
    white: "#FFFFFF",
    orange: "#F97316",
    deepOrange: "#D94801",
    gold: "#FFB000",
    light: "#F7FAFC",
    softBlue: "#EAF8FF",
    softOrange: "#FFF1E8",
    text: "#17212B",
    muted: "#657786",
    border: "#DCE7ED",
    grid: "rgba(39,199,245,.08)",
};

const EnergyParticle = ({
    top,
    left,
    right,
    bottom,
    size = 7,
    delay = 0,
    color = COLORS.cyan,
}) => (
    <motion.div
        initial={{
            opacity: 0,
            scale: 0.2,
        }}
        animate={{
            opacity: [0, 1, 0.7, 0],
            scale: [0.2, 1, 1.25, 0.1],
            x: [0, -10, 12, -20],
            y: [0, -12, -26, -42],
        }}
        transition={{
            duration: 3.5,
            delay,
            repeat: Infinity,
            ease: "easeOut",
        }}
        style={{
            position: "absolute",
            top,
            left,
            right,
            bottom,
            width: size,
            height: size,
            borderRadius: "50%",
            background: color,
            boxShadow: `0 0 18px ${color}`,
            zIndex: 15,
        }}
    />
);

const SpeedLine = ({
    top,
    left,
    right,
    width = 120,
    rotate = -20,
    delay = 0,
    color = COLORS.cyan,
}) => (
    <motion.div
        initial={{
            opacity: 0,
            x: 0,
            scaleX: 0.3,
        }}
        animate={{
            opacity: [0, 0.9, 0],
            x: [0, 35, 75],
            scaleX: [0.3, 1, 0.5],
        }}
        transition={{
            duration: 2.2,
            delay,
            repeat: Infinity,
            ease: "easeOut",
        }}
        style={{
            position: "absolute",
            top,
            left,
            right,
            width,
            height: 3,
            borderRadius: 20,
            background: `linear-gradient(90deg, transparent, ${color})`,
            transform: `rotate(${rotate}deg)`,
            transformOrigin: "left center",
            zIndex: 10,
        }}
    />
);

const OrbitEnergy = ({
    size,
    duration,
    reverse = false,
    color,
    secondaryColor,
    delay = 0,
    dashed = false,
}) => (
    <motion.div
        animate={{
            rotate: reverse ? -360 : 360,
        }}
        transition={{
            duration,
            delay,
            repeat: Infinity,
            ease: "linear",
        }}
        style={{
            position: "absolute",
            width: size,
            height: size,
            borderRadius: "50%",
            border: dashed
                ? `1px dashed ${color}`
                : `1px solid ${color}`,
            borderTopColor: secondaryColor || color,
            borderRightColor: secondaryColor || color,
            zIndex: 7,
            pointerEvents: "none",
        }}
    >
        <motion.div
            animate={{
                scale: [0.75, 1.25, 0.75],
                opacity: [0.55, 1, 0.55],
            }}
            transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
            }}
            style={{
                position: "absolute",
                top: -6,
                left: "50%",
                transform: "translateX(-50%)",
                width: 11,
                height: 11,
                borderRadius: "50%",
                background: secondaryColor || color,
                boxShadow: `0 0 20px ${secondaryColor || color}`,
            }}
        />

        <Box
            sx={{
                position: "absolute",
                right: -5,
                top: "50%",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: color,
                boxShadow: `0 0 16px ${color}`,
                transform: "translateY(-50%)",
            }}
        />

        <Box
            sx={{
                position: "absolute",
                bottom: -4,
                left: "20%",
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: secondaryColor || color,
                boxShadow: `0 0 15px ${secondaryColor || color}`,
            }}
        />
    </motion.div>
);

const Login = () => {
    const navigate = useNavigate();
    const { setUser } = useContext(AuthContext);

    const isMobile = useMediaQuery("(max-width:900px)");

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const redirectUser = (user) => {
        const role = String(
            user?.user_type || user?.role || ""
        )
            .toLowerCase()
            .trim();

        switch (role) {
            case "owner":
                navigate("/owner-dashboard");
                break;

            case "admin":
                navigate("/newAdminDashboard");
                break;

            case "teacher":
                navigate("/teacher-dashboard");
                break;

            case "student":
                navigate("/student-dashboard");
                break;

            case "parent":
                navigate("/parentsDashboard");
                break;

            case "registry":
                navigate("/registerDashboard");
                break;

            case "principal":
                navigate("/principal-dashboard");
                break;

            case "proprietor":
                navigate("/proprietor-dashboard");
                break;

            case "vpi":
                navigate("/vpi-dashboard");
                break;

            case "vpa":
                navigate("/newVpaDashboard");
                break;

            case "it personel":
            case "it personnel":
                navigate("/it-dashboard");
                break;

            case "business_manager":
            case "business manager":
                navigate("/businessManagerDashboard");
                break;

            case "admission officer":
            case "admission_officer":
                navigate("/admissionOfficialDashboard");
                break;

            case "dean":
                navigate("/dean-dashboard");
                break;

            default:
                navigate("/");
        }
    };

    const clearAuthenticationData = () => {
        localStorage.removeItem("access");
        localStorage.removeItem("refresh");
        localStorage.removeItem("user");
        localStorage.removeItem("employment_status");
        localStorage.removeItem("employment_status_display");
        localStorage.removeItem("employment_academic_year");
        localStorage.removeItem("auth_email");
    };

    const getLoginError = (error) => {
        const responseData = error?.response?.data;

        if (responseData?.detail) {
            return String(responseData.detail);
        }

        if (responseData?.message) {
            return String(responseData.message);
        }

        if (typeof responseData === "string") {
            return responseData;
        }

        if (responseData && typeof responseData === "object") {
            const messages = [];

            Object.entries(responseData).forEach(
                ([field, value]) => {
                    if (field === "detail") {
                        return;
                    }

                    if (Array.isArray(value)) {
                        value.forEach((item) => {
                            if (
                                item !== null &&
                                item !== undefined
                            ) {
                                messages.push(String(item));
                            }
                        });
                    } else if (typeof value === "string") {
                        messages.push(value);
                    }
                }
            );

            if (messages.length) {
                return messages.join("\n");
            }
        }

        return "Invalid email or password.";
    };

    const showEmploymentStatusAlert = (data) => {
        const errorCode = String(
            data?.error_code || ""
        )
            .toUpperCase()
            .trim();

        const status = String(
            data?.employment_status || ""
        )
            .toLowerCase()
            .trim();

        const academicYear =
            data?.academic_year ||
            data?.academic_year_name ||
            "the current academic year";

        const statusDisplay =
            data?.employment_status_display ||
            (status === "fired"
                ? "SACKED / FIRED"
                : status === "removed"
                    ? "REMOVED"
                    : "");

        if (
            errorCode === "TEACHER_FIRED" ||
            errorCode === "EMPLOYMENT_FIRED" ||
            status === "fired"
        ) {
            Swal.fire({
                icon: "error",
                title: "Account Blocked!",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your account cannot be used to access
                            this POLU TECH platform.
                        </p>

                        <div style="
                            display:inline-block;
                            padding:8px 16px;
                            border-radius:20px;
                            background:#FEF2F2;
                            color:#B91C1C;
                            font-weight:800;
                            margin:10px 0 14px;
                        ">
                            EMPLOYMENT STATUS:
                            ${statusDisplay || "SACKED / FIRED"}
                        </div>

                        <p style="
                            color:#6B7280;
                            line-height:1.6;
                        ">
                            Your most recent employment record
                            indicates that your employment was
                            terminated.
                        </p>

                        <p style="
                            color:#374151;
                            font-weight:600;
                            line-height:1.6;
                        ">
                            Please contact an authorized administrator
                            for assistance.
                        </p>

                        <p style="
                            color:#6B7280;
                            font-size:13px;
                            margin-top:12px;
                        ">
                            Latest employment record:
                            <strong>${academicYear}</strong>
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.orange,
            });

            return true;
        }

        if (
            errorCode === "TEACHER_REMOVED" ||
            errorCode === "EMPLOYMENT_REMOVED" ||
            status === "removed"
        ) {
            Swal.fire({
                icon: "warning",
                title: "Account Blocked!",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your account cannot be used to access
                            this POLU TECH platform.
                        </p>

                        <div style="
                            display:inline-block;
                            padding:8px 16px;
                            border-radius:20px;
                            background:#FFF7ED;
                            color:#C2410C;
                            font-weight:800;
                            margin:10px 0 14px;
                        ">
                            EMPLOYMENT STATUS:
                            ${statusDisplay || "REMOVED"}
                        </div>

                        <p style="
                            color:#6B7280;
                            line-height:1.6;
                        ">
                            Your most recent employment record
                            indicates that you have been removed
                            from the organization.
                        </p>

                        <p style="
                            color:#374151;
                            font-weight:600;
                            line-height:1.6;
                        ">
                            Please contact an authorized administrator
                            for assistance.
                        </p>

                        <p style="
                            color:#6B7280;
                            font-size:13px;
                            margin-top:12px;
                        ">
                            Latest employment record:
                            <strong>${academicYear}</strong>
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.orange,
            });

            return true;
        }

        if (errorCode === "TEACHER_NOT_ASSIGNED") {
            Swal.fire({
                icon: "warning",
                title: "Teacher Not Assigned",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your teacher account is not currently
                            assigned to an active academic year.
                        </p>

                        <p style="
                            font-size:17px;
                            font-weight:800;
                            color:${COLORS.blue};
                        ">
                            ${academicYear}
                        </p>

                        <p style="color:#6B7280;line-height:1.6;">
                            Please contact the school administration
                            to have your teacher record assigned.
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.orange,
            });

            return true;
        }

        if (errorCode === "NO_ACTIVE_ACADEMIC_YEAR") {
            Swal.fire({
                icon: "warning",
                title: "No Active Academic Year",
                text:
                    "There is currently no active academic year. Please contact the school administration.",
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.orange,
            });

            return true;
        }

        if (
            errorCode === "EMPLOYMENT_LOGIN_BLOCKED" ||
            errorCode === "LOGIN_BLOCKED"
        ) {
            Swal.fire({
                icon: "warning",
                title: "Login Blocked",
                html: `
                    <div style="text-align:center;">
                        <p style="font-size:16px;line-height:1.6;">
                            Your account is currently blocked from
                            accessing this platform.
                        </p>

                        <p style="
                            color:#374151;
                            font-weight:600;
                            line-height:1.6;
                        ">
                            Please contact an authorized administrator.
                        </p>
                    </div>
                `,
                confirmButtonText: "Understood",
                confirmButtonColor: COLORS.orange,
            });

            return true;
        }

        return false;
    };

    const handleBlockedResponse = (data) => {
        if (!data) {
            return false;
        }

        const status = String(
            data?.employment_status || ""
        )
            .toLowerCase()
            .trim();

        const errorCode = String(
            data?.error_code || ""
        )
            .toUpperCase()
            .trim();

        const employmentCodes = [
            "TEACHER_REMOVED",
            "TEACHER_FIRED",
            "EMPLOYMENT_REMOVED",
            "EMPLOYMENT_FIRED",
            "EMPLOYMENT_LOGIN_BLOCKED",
            "LOGIN_BLOCKED",
            "TEACHER_NOT_ASSIGNED",
            "NO_ACTIVE_ACADEMIC_YEAR",
        ];

        const isEmploymentError =
            employmentCodes.includes(errorCode) ||
            ["removed", "fired"].includes(status);

        if (!isEmploymentError) {
            return false;
        }

        clearAuthenticationData();
        return showEmploymentStatusAlert(data);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanEmail = email.trim();

        if (!cleanEmail || !password) {
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text:
                    "Please enter your email address and password.",
                confirmButtonColor: COLORS.orange,
            });

            return;
        }

        setLoading(true);

        try {
            clearAuthenticationData();

            const response = await api.post(
                "/api/token/",
                {
                    email: cleanEmail,
                    password,
                }
            );

            console.log(
                "TOKEN RESPONSE:",
                response.data
            );

            if (
                handleBlockedResponse(
                    response.data
                )
            ) {
                return;
            }

            const role = String(
                response.data?.user_type ||
                response.data?.user?.role ||
                ""
            )
                .toLowerCase()
                .trim();

            const authenticationRoles = [
                "admin",
                "registry",
                "principal",
                "proprietor",
                "business_manager",
                "business manager",
                "vpi",
                "vpa",
                "it personel",
                "it personnel",
            ];

            if (
                response.data?.requires_authentication &&
                authenticationRoles.includes(role)
            ) {
                const authEmail =
                    response.data?.email ||
                    response.data?.user?.email ||
                    cleanEmail;

                localStorage.setItem(
                    "auth_email",
                    authEmail
                );

                navigate(
                    "/loginAuthentication",
                    {
                        state: {
                            email: authEmail,
                        },
                    }
                );

                return;
            }

            const access =
                response.data?.access;

            const refresh =
                response.data?.refresh;

            if (!access || !refresh) {
                throw new Error(
                    "Authentication tokens were not returned."
                );
            }

            localStorage.setItem(
                "access",
                access
            );

            localStorage.setItem(
                "refresh",
                refresh
            );

            if (
                response.data?.employment_status
            ) {
                localStorage.setItem(
                    "employment_status",
                    response.data.employment_status
                );
            }

            if (
                response.data
                    ?.employment_status_display
            ) {
                localStorage.setItem(
                    "employment_status_display",
                    response.data
                        .employment_status_display
                );
            }

            if (
                response.data?.academic_year
            ) {
                localStorage.setItem(
                    "employment_academic_year",
                    response.data.academic_year
                );
            }

            window.dispatchEvent(
                new Event("user-logged-in")
            );

            let profileResponse;

            try {
                profileResponse =
                    await api.get(
                        "/api/user-profile/"
                    );
            } catch (profileError) {
                clearAuthenticationData();
                throw profileError;
            }

            const user =
                profileResponse.data;

            const frontendStatus = String(
                response.data
                    ?.employment_status ||
                user?.employment_status ||
                ""
            )
                .toLowerCase()
                .trim();

            if (
                frontendStatus === "removed" ||
                frontendStatus === "fired"
            ) {
                clearAuthenticationData();

                showEmploymentStatusAlert({
                    error_code:
                        frontendStatus === "removed"
                            ? "TEACHER_REMOVED"
                            : "TEACHER_FIRED",
                    employment_status:
                        frontendStatus,
                    employment_status_display:
                        frontendStatus === "removed"
                            ? "REMOVED"
                            : "SACKED / FIRED",
                    academic_year:
                        response.data?.academic_year ||
                        response.data
                            ?.academic_year_name,
                });

                return;
            }

            setUser(user);

            localStorage.setItem(
                "user",
                JSON.stringify(user)
            );

            Swal.fire({
                icon: "success",
                title: "Welcome to POLU TECH!",
                text: `Welcome ${
                    user?.user_type ||
                    user?.role ||
                    ""
                }`,
                timer: 1800,
                showConfirmButton: false,
                background: COLORS.white,
                color: COLORS.black,
                iconColor: COLORS.orange,
            });

            setTimeout(() => {
                redirectUser(user);
            }, 1700);
        } catch (error) {
            console.error(
                "LOGIN ERROR:",
                error
            );

            const responseData =
                error?.response?.data;

            if (responseData) {
                const handled =
                    handleBlockedResponse(
                        responseData
                    );

                if (handled) {
                    return;
                }
            }

            Swal.fire({
                icon: "error",
                title: "Login Failed",
                text: getLoginError(error),
                confirmButtonText: "Try Again",
                confirmButtonColor: COLORS.orange,
                background: COLORS.white,
                color: COLORS.black,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: "100dvh",
                overflow: "hidden",
                background: COLORS.light,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    minHeight: "100dvh",
                    display: "flex",
                    flexDirection: {
                        xs: "column",
                        md: "row",
                    },
                }}
            >
                {/* TECHNOLOGY PANEL */}
                <Box
                    sx={{
                        display: {
                            xs: "none",
                            md: "flex",
                        },
                        position: "relative",
                        width: "53%",
                        height: "100dvh",
                        flexDirection: "column",
                        overflow: "hidden",
                        color: COLORS.white,
                        background:
                            "linear-gradient(145deg, #050505 0%, #071426 40%, #06283A 72%, #03121F 100%)",
                    }}
                >
                    {/* TECHNOLOGY GRID */}
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0.8,
                            backgroundImage: `
                                linear-gradient(${COLORS.grid} 1px, transparent 1px),
                                linear-gradient(90deg, ${COLORS.grid} 1px, transparent 1px)
                            `,
                            backgroundSize: "42px 42px",
                            maskImage:
                                "linear-gradient(to bottom, black, transparent)",
                        }}
                    />

                    {/* TOP GLOW */}
                    <motion.div
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.1, 0.3, 0.1],
                        }}
                        transition={{
                            duration: 8,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        style={{
                            position: "absolute",
                            width: 650,
                            height: 650,
                            borderRadius: "50%",
                            top: -360,
                            left: -320,
                            background:
                                "radial-gradient(circle, rgba(39,199,245,.5), transparent 68%)",
                        }}
                    />

                    {/* ORANGE GLOW */}
                    <motion.div
                        animate={{
                            scale: [1, 1.22, 1],
                            opacity: [0.08, 0.25, 0.08],
                        }}
                        transition={{
                            duration: 9,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        style={{
                            position: "absolute",
                            width: 600,
                            height: 600,
                            borderRadius: "50%",
                            right: -320,
                            bottom: -320,
                            background:
                                "radial-gradient(circle, rgba(249,115,22,.32), transparent 68%)",
                        }}
                    />

                    {/* BRAND */}
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 30,
                            textAlign: "center",
                            pt: 5,
                            px: 4,
                        }}
                    >
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1.5,
                                px: 2.2,
                                py: 1,
                                borderRadius: "50px",
                                background:
                                    "rgba(255,255,255,.07)",
                                border:
                                    "1px solid rgba(255,255,255,.16)",
                                backdropFilter:
                                    "blur(12px)",
                            }}
                        >
                            <Code
                                sx={{
                                    fontSize: 24,
                                    color: COLORS.orange,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: ".72rem",
                                    fontWeight: 900,
                                    letterSpacing: 2,
                                    color: COLORS.white,
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        color: COLORS.orange,
                                        fontWeight: 1000,
                                    }}
                                >
                                    POLU
                                </Box>{" "}
                                TECH
                            </Typography>
                        </Box>

                        <Typography
                            sx={{
                                mt: 2.5,
                                fontSize: {
                                    md: "1.65rem",
                                    lg: "2rem",
                                },
                                fontWeight: 950,
                                letterSpacing: 1.5,
                                lineHeight: 1.15,
                                textTransform: "uppercase",
                            }}
                        >
                            Integrated Technology
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.7,
                                color: "#8EDFFF",
                                fontSize: {
                                    md: ".9rem",
                                    lg: "1rem",
                                },
                                fontWeight: 800,
                                letterSpacing: 2,
                            }}
                        >
                            DIGITAL SOLUTIONS
                        </Typography>

                        <Box
                            sx={{
                                width: 90,
                                height: 4,
                                borderRadius: 20,
                                mx: "auto",
                                mt: 2,
                                background:
                                    "linear-gradient(90deg, #27C7F5, #F97316)",
                            }}
                        />
                    </Box>

                    {/* ENERGY AREA */}
                    <Box
                        sx={{
                            position: "relative",
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <OrbitEnergy
                            size={500}
                            duration={17}
                            color="rgba(39,199,245,.18)"
                            secondaryColor={COLORS.orange}
                        />

                        <OrbitEnergy
                            size={420}
                            duration={11}
                            reverse
                            color="rgba(39,199,245,.55)"
                            secondaryColor="#FFFFFF"
                            dashed
                        />

                        <OrbitEnergy
                            size={350}
                            duration={8}
                            color="rgba(249,115,22,.7)"
                            secondaryColor="#FFFFFF"
                        />

                        <OrbitEnergy
                            size={280}
                            duration={6}
                            reverse
                            color="rgba(255,255,255,.2)"
                            secondaryColor={COLORS.orange}
                            dashed
                        />

                        <EnergyParticle
                            top="24%"
                            left="27%"
                            size={9}
                        />

                        <EnergyParticle
                            top="31%"
                            right="26%"
                            size={7}
                            delay={0.7}
                            color={COLORS.orange}
                        />

                        <EnergyParticle
                            top="43%"
                            left="16%"
                            size={6}
                            delay={1.3}
                        />

                        <EnergyParticle
                            top="49%"
                            right="16%"
                            size={9}
                            delay={1.9}
                        />

                        <EnergyParticle
                            top="60%"
                            left="24%"
                            size={7}
                            delay={2.5}
                            color={COLORS.orange}
                        />

                        <EnergyParticle
                            top="57%"
                            right="23%"
                            size={6}
                            delay={3}
                        />

                        <SpeedLine
                            top="28%"
                            left="8%"
                            width={170}
                            rotate={-22}
                        />

                        <SpeedLine
                            top="34%"
                            left="4%"
                            width={110}
                            rotate={-19}
                            delay={0.6}
                            color={COLORS.orange}
                        />

                        <SpeedLine
                            top="42%"
                            right="5%"
                            width={155}
                            rotate={-26}
                            delay={1.1}
                        />

                        <SpeedLine
                            top="49%"
                            right="8%"
                            width={100}
                            rotate={-24}
                            delay={1.7}
                            color={COLORS.orange}
                        />

                        <SpeedLine
                            top="60%"
                            left="9%"
                            width={125}
                            rotate={-16}
                            delay={2.1}
                        />

                        <SpeedLine
                            top="64%"
                            right="10%"
                            width={130}
                            rotate={-20}
                            delay={2.5}
                        />

                        {/* CENTRAL TECHNOLOGY GLOW */}
                        <Box
                            sx={{
                                position: "absolute",
                                width: 430,
                                height: 430,
                                borderRadius: "50%",
                                background:
                                    "radial-gradient(circle, rgba(39,199,245,.22), rgba(3,27,78,.04) 55%, transparent 72%)",
                                zIndex: 4,
                            }}
                        />

                        {/* TECHNOLOGY ICONS */}
                        <motion.div
                            animate={{
                                y: [-10, 10, -10],
                                rotate: [-3, 3, -3],
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            style={{
                                position: "absolute",
                                zIndex: 20,
                                top: "17%",
                                left: "22%",
                            }}
                        >
                            <Terminal
                                sx={{
                                    fontSize: 48,
                                    color: COLORS.cyan,
                                    filter:
                                        "drop-shadow(0 0 18px rgba(39,199,245,.8))",
                                }}
                            />
                        </motion.div>

                        <motion.div
                            animate={{
                                y: [10, -10, 10],
                                rotate: [3, -3, 3],
                            }}
                            transition={{
                                duration: 4.5,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            style={{
                                position: "absolute",
                                zIndex: 20,
                                right: "20%",
                                bottom: "18%",
                            }}
                        >
                            <Storage
                                sx={{
                                    fontSize: 45,
                                    color: COLORS.orange,
                                    filter:
                                        "drop-shadow(0 0 18px rgba(249,115,22,.7))",
                                }}
                            />
                        </motion.div>

                        <motion.div
                            animate={{
                                scale: [0.8, 1.2, 0.8],
                                rotate: [-10, 8, -10],
                                opacity: [0.4, 1, 0.4],
                            }}
                            transition={{
                                duration: 2.2,
                                repeat: Infinity,
                            }}
                            style={{
                                position: "absolute",
                                right: "20%",
                                top: "28%",
                                zIndex: 25,
                            }}
                        >
                            <Bolt
                                sx={{
                                    fontSize: 42,
                                    color: COLORS.white,
                                    filter:
                                        "drop-shadow(0 0 14px rgba(255,255,255,.8))",
                                }}
                            />
                        </motion.div>

                        <motion.div
                            animate={{
                                scale: [0.7, 1.3, 0.7],
                                rotate: [0, 180, 360],
                                opacity: [0.35, 1, 0.35],
                            }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            style={{
                                position: "absolute",
                                left: "20%",
                                top: "38%",
                                zIndex: 25,
                            }}
                        >
                            <AutoAwesome
                                sx={{
                                    fontSize: 34,
                                    color: COLORS.orange,
                                }}
                            />
                        </motion.div>

                        {/* CENTRAL IMAGE */}
                        <Box
                            component="img"
                            src={student}
                            alt="POLU technology platform"
                            sx={{
                                position: "relative",
                                zIndex: 20,
                                width: {
                                    md: "74%",
                                    lg: "78%",
                                },
                                maxWidth: 700,
                                height: "82%",
                                objectFit: "contain",
                                objectPosition: "center bottom",
                                filter:
                                    "drop-shadow(0 30px 35px rgba(0,0,0,.5))",
                                userSelect: "none",
                                pointerEvents: "none",
                            }}
                        />

                        {/* FLOATING TECHNOLOGY LABELS */}
                        <Box
                            sx={{
                                position: "absolute",
                                zIndex: 30,
                                left: "7%",
                                bottom: "20%",
                                px: 1.5,
                                py: 0.8,
                                borderRadius: "12px",
                                background:
                                    "rgba(5,15,25,.8)",
                                border:
                                    "1px solid rgba(39,199,245,.25)",
                                backdropFilter:
                                    "blur(10px)",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.7,
                            }}
                        >
                            <Lan
                                sx={{
                                    fontSize: 16,
                                    color: COLORS.cyan,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: ".6rem",
                                    fontWeight: 900,
                                    letterSpacing: 1,
                                    color: COLORS.white,
                                }}
                            >
                                CONNECTED
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                position: "absolute",
                                zIndex: 30,
                                right: "7%",
                                top: "61%",
                                px: 1.5,
                                py: 0.8,
                                borderRadius: "12px",
                                background:
                                    "rgba(5,15,25,.8)",
                                border:
                                    "1px solid rgba(249,115,22,.3)",
                                backdropFilter:
                                    "blur(10px)",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.7,
                            }}
                        >
                            <Hub
                                sx={{
                                    fontSize: 16,
                                    color: COLORS.orange,
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: ".6rem",
                                    fontWeight: 900,
                                    letterSpacing: 1,
                                    color: COLORS.white,
                                }}
                            >
                                DIGITAL
                            </Typography>
                        </Box>
                    </Box>

                    {/* BOTTOM */}
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 20,
                            textAlign: "center",
                            pb: 3,
                        }}
                    >
                        <Typography
                            sx={{
                                color:
                                    "rgba(255,255,255,.65)",
                                fontSize: ".68rem",
                                fontWeight: 800,
                                letterSpacing: 1.5,
                            }}
                        >
                            CODE • CONNECT • CREATE • TRANSFORM
                        </Typography>
                    </Box>
                </Box>

                {/* MOBILE BRAND */}
                <Box
                    sx={{
                        display: {
                            xs: "flex",
                            md: "none",
                        },
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 30,
                        justifyContent: "center",
                        pt: 2,
                        pointerEvents: "none",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 0.9,
                            borderRadius: "50px",
                            background:
                                "rgba(255,255,255,.96)",
                            border:
                                `1px solid ${COLORS.border}`,
                            boxShadow:
                                "0 8px 25px rgba(0,0,0,.08)",
                        }}
                    >
                        <Code
                            sx={{
                                fontSize: 21,
                                color: COLORS.orange,
                            }}
                        />

                        <Typography
                            sx={{
                                fontSize: ".7rem",
                                fontWeight: 950,
                                color: COLORS.black,
                                letterSpacing: 1,
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    color: COLORS.orange,
                                }}
                            >
                                POLU
                            </Box>{" "}
                            TECH
                        </Typography>
                    </Box>
                </Box>

                {/* LOGIN AREA */}
                <Box
                    sx={{
                        position: "relative",
                        width: {
                            xs: "100%",
                            md: "47%",
                        },
                        minHeight: "100dvh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxSizing: "border-box",
                        overflowY: "auto",
                        overflowX: "hidden",
                        px: {
                            xs: 1.5,
                            sm: 3,
                            md: 5,
                        },
                        py: {
                            xs: 6,
                            sm: 5,
                            md: 4,
                        },
                        background:
                            "linear-gradient(135deg, #FFFFFF 0%, #F8FBFD 58%, #EEF8FC 100%)",
                    }}
                >
                    {/* BACKGROUND DECORATION */}
                    <motion.div
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.08, 0.18, 0.08],
                        }}
                        transition={{
                            duration: 7,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        style={{
                            position: "absolute",
                            width: 400,
                            height: 400,
                            borderRadius: "50%",
                            background:
                                "radial-gradient(circle, rgba(39,199,245,.22), transparent 68%)",
                            top: -230,
                            right: -210,
                        }}
                    />

                    <motion.div
                        animate={{
                            scale: [0.9, 1.15, 0.9],
                            opacity: [0.07, 0.17, 0.07],
                        }}
                        transition={{
                            duration: 8,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        style={{
                            position: "absolute",
                            width: 350,
                            height: 350,
                            borderRadius: "50%",
                            background:
                                "radial-gradient(circle, rgba(249,115,22,.18), transparent 68%)",
                            bottom: -200,
                            left: -200,
                        }}
                    />

                    {/* LOGIN CARD */}
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            width: "100%",
                            maxWidth: 460,
                            mx: "auto",
                        }}
                    >
                        <motion.div
                            initial={{
                                opacity: 0,
                                y: 25,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            transition={{
                                duration: 0.65,
                                ease: "easeOut",
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    boxSizing: "border-box",
                                    overflow: "hidden",
                                    p: {
                                        xs: 2.4,
                                        sm: 3.5,
                                        md: 4,
                                    },
                                    borderRadius: {
                                        xs: "20px",
                                        sm: "26px",
                                        md: "30px",
                                    },
                                    background:
                                        "rgba(255,255,255,.98)",
                                    border:
                                        `1px solid ${COLORS.border}`,
                                    boxShadow:
                                        "0 25px 65px rgba(6,17,31,.11)",
                                }}
                            >
                                {/* TOP ACCENT */}
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: 5,
                                        background:
                                            `linear-gradient(90deg, ${COLORS.black}, ${COLORS.blue}, ${COLORS.orange})`,
                                    }}
                                />

                                {/* HEADER */}
                                <Box
                                    sx={{
                                        textAlign: "center",
                                        mb: 3,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent:
                                                "center",
                                            gap: 1.25,
                                            width: "100%",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                flexShrink: 0,
                                                borderRadius: "17px",
                                                display: "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                background:
                                                    `linear-gradient(135deg, ${COLORS.black}, ${COLORS.deepBlue})`,
                                                boxShadow:
                                                    "0 10px 25px rgba(0,0,0,.16)",
                                                position:
                                                    "relative",
                                            }}
                                        >
                                            <Code
                                                sx={{
                                                    fontSize: 27,
                                                    color:
                                                        COLORS.white,
                                                }}
                                            />

                                            <Box
                                                sx={{
                                                    position:
                                                        "absolute",
                                                    width: 9,
                                                    height: 9,
                                                    borderRadius:
                                                        "50%",
                                                    background:
                                                        COLORS.orange,
                                                    right: -2,
                                                    top: -2,
                                                    border:
                                                        `3px solid ${COLORS.white}`,
                                                }}
                                            />
                                        </Box>

                                        <Typography
                                            component="div"
                                            sx={{
                                                display: "flex",
                                                alignItems:
                                                    "baseline",
                                                gap: 0.65,
                                                whiteSpace:
                                                    "nowrap",
                                                fontFamily:
                                                    '"Arial Black", "Montserrat", "Segoe UI", sans-serif',
                                                fontWeight: 900,
                                                letterSpacing:
                                                    "-0.25px",
                                                fontSize: {
                                                    xs: ".9rem",
                                                    sm: "1.05rem",
                                                    md: "1.1rem",
                                                },
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    color:
                                                        COLORS.orange,
                                                    fontWeight:
                                                        950,
                                                }}
                                            >
                                                POLU
                                            </Box>

                                            <Box
                                                component="span"
                                                sx={{
                                                    color:
                                                        COLORS.black,
                                                    fontWeight:
                                                        850,
                                                    letterSpacing:
                                                        ".8px",
                                                }}
                                            >
                                                TECH
                                            </Box>
                                        </Typography>
                                    </Box>

                                    <Typography
                                        sx={{
                                            mt: 2,
                                            fontSize: {
                                                xs: "1.55rem",
                                                sm: "1.85rem",
                                            },
                                            fontWeight: 950,
                                            color:
                                                COLORS.text,
                                        }}
                                    >
                                        Welcome Back
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color:
                                                COLORS.muted,
                                            mt: 0.8,
                                            lineHeight: 1.65,
                                            fontSize: ".87rem",
                                        }}
                                    >
                                        Sign in to securely access
                                        your
                                        <br />

                                        <strong
                                            style={{
                                                color:
                                                    COLORS.blue,
                                            }}
                                        >
                                            POLU TECH Digital Platform
                                        </strong>
                                    </Typography>
                                </Box>

                                {/* SECURITY BADGE */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                        gap: 0.8,
                                        mb: 2.5,
                                        py: 0.9,
                                        px: 1.5,
                                        borderRadius: "50px",
                                        background:
                                            COLORS.softOrange,
                                        border:
                                            "1px solid rgba(249,115,22,.14)",
                                    }}
                                >
                                    <Security
                                        sx={{
                                            fontSize: 16,
                                            color:
                                                COLORS.orange,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            fontSize: ".62rem",
                                            fontWeight: 900,
                                            color:
                                                COLORS.deepOrange,
                                            letterSpacing:
                                                0.6,
                                            textAlign:
                                                "center",
                                        }}
                                    >
                                        SECURE POLU TECH AUTHENTICATION
                                    </Typography>
                                </Box>

                                {/* FORM */}
                                <form onSubmit={handleSubmit}>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        variant="outlined"
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(
                                                e.target.value
                                            )
                                        }
                                        required
                                        autoComplete="email"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email
                                                        sx={{
                                                            color:
                                                                COLORS.blue,
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            mb: 2,
                                            "& .MuiOutlinedInput-root":
                                                {
                                                    borderRadius:
                                                        "14px",
                                                    background:
                                                        "#FAFCFD",
                                                    "& fieldset":
                                                        {
                                                            borderColor:
                                                                COLORS.border,
                                                        },
                                                    "&:hover fieldset":
                                                        {
                                                            borderColor:
                                                                "#8BCFE3",
                                                        },
                                                    "&.Mui-focused fieldset":
                                                        {
                                                            borderColor:
                                                                COLORS.orange,
                                                            borderWidth: 2,
                                                        },
                                                },
                                            "& .MuiInputLabel-root.Mui-focused":
                                                {
                                                    color:
                                                        COLORS.orange,
                                                },
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Password"
                                        type={
                                            showPassword
                                                ? "text"
                                                : "password"
                                        }
                                        variant="outlined"
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(
                                                e.target.value
                                            )
                                        }
                                        required
                                        autoComplete="current-password"
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock
                                                        sx={{
                                                            color:
                                                                COLORS.blue,
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() =>
                                                            setShowPassword(
                                                                (prev) =>
                                                                    !prev
                                                            )
                                                        }
                                                        edge="end"
                                                        aria-label={
                                                            showPassword
                                                                ? "Hide password"
                                                                : "Show password"
                                                        }
                                                    >
                                                        {showPassword ? (
                                                            <VisibilityOff
                                                                sx={{
                                                                    color:
                                                                        COLORS.muted,
                                                                }}
                                                            />
                                                        ) : (
                                                            <Visibility
                                                                sx={{
                                                                    color:
                                                                        COLORS.muted,
                                                                }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root":
                                                {
                                                    borderRadius:
                                                        "14px",
                                                    background:
                                                        "#FAFCFD",
                                                    "& fieldset":
                                                        {
                                                            borderColor:
                                                                COLORS.border,
                                                        },
                                                    "&:hover fieldset":
                                                        {
                                                            borderColor:
                                                                "#8BCFE3",
                                                        },
                                                    "&.Mui-focused fieldset":
                                                        {
                                                            borderColor:
                                                                COLORS.orange,
                                                            borderWidth: 2,
                                                        },
                                                },
                                            "& .MuiInputLabel-root.Mui-focused":
                                                {
                                                    color:
                                                        COLORS.orange,
                                                },
                                        }}
                                    />

                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent:
                                                "flex-end",
                                            mt: 1.3,
                                            mb: 2.7,
                                        }}
                                    >
                                        <Link
                                            to="/forgotPassword"
                                            style={{
                                                color:
                                                    COLORS.deepOrange,
                                                fontWeight: 850,
                                                textDecoration:
                                                    "none",
                                                fontSize:
                                                    ".84rem",
                                            }}
                                        >
                                            Forgot Password?
                                        </Link>
                                    </Box>

                                    <Button
                                        type="submit"
                                        fullWidth
                                        disabled={loading}
                                        variant="contained"
                                        sx={{
                                            py: 1.55,
                                            borderRadius:
                                                "14px",
                                            fontSize:
                                                ".97rem",
                                            fontWeight: 900,
                                            textTransform:
                                                "none",
                                            color:
                                                COLORS.white,
                                            background:
                                                `linear-gradient(135deg, ${COLORS.black}, ${COLORS.deepBlue})`,
                                            boxShadow:
                                                "0 14px 30px rgba(0,0,0,.18)",
                                            transition:
                                                "all .25s ease",
                                            "&:hover":
                                                {
                                                    background:
                                                        `linear-gradient(135deg, ${COLORS.deepOrange}, ${COLORS.orange})`,
                                                    boxShadow:
                                                        "0 16px 35px rgba(249,115,22,.28)",
                                                    transform:
                                                        "translateY(-2px)",
                                                },
                                            "&:disabled":
                                                {
                                                    background:
                                                        "#9AA6AD",
                                                },
                                        }}
                                    >
                                        {loading ? (
                                            <Box
                                                sx={{
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                    gap: 1.3,
                                                }}
                                            >
                                                <CircularProgress
                                                    size={21}
                                                    thickness={5}
                                                    sx={{
                                                        color:
                                                            COLORS.white,
                                                    }}
                                                />

                                                Signing In...
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                    gap: 0.9,
                                                }}
                                            >
                                                <AutoAwesome
                                                    sx={{
                                                        fontSize:
                                                            18,
                                                    }}
                                                />

                                                Sign In Securely
                                            </Box>
                                        )}
                                    </Button>

                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() =>
                                            navigate("/")
                                        }
                                        startIcon={
                                            <ArrowBack />
                                        }
                                        sx={{
                                            mt: 1.5,
                                            py: 1.35,
                                            borderRadius:
                                                "14px",
                                            fontSize:
                                                ".9rem",
                                            fontWeight: 800,
                                            textTransform:
                                                "none",
                                            color:
                                                COLORS.deepBlue,
                                            border:
                                                `1.5px solid ${COLORS.border}`,
                                            "&:hover":
                                                {
                                                    background:
                                                        COLORS.softOrange,
                                                    borderColor:
                                                        COLORS.orange,
                                                    color:
                                                        COLORS.deepOrange,
                                                },
                                        }}
                                    >
                                        Back to POLU TECH
                                    </Button>
                                </form>

                                {/* FOOTER */}
                                <Box
                                    sx={{
                                        mt: 3,
                                        pt: 2.2,
                                        borderTop:
                                            `1px solid ${COLORS.border}`,
                                        textAlign:
                                            "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",
                                            gap: 0.7,
                                            mb: 0.6,
                                        }}
                                    >
                                        <VerifiedUser
                                            sx={{
                                                fontSize:
                                                    14,
                                                color:
                                                    COLORS.blue,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                fontSize:
                                                    ".66rem",
                                                fontWeight:
                                                    850,
                                                color:
                                                    COLORS.blue,
                                                letterSpacing:
                                                    0.5,
                                            }}
                                        >
                                            SECURE AUTHENTICATION
                                        </Typography>
                                    </Box>

                                    <Typography
                                        sx={{
                                            fontSize:
                                                ".68rem",
                                            color:
                                                "#8AA0AA",
                                            lineHeight:
                                                1.6,
                                        }}
                                    >
                                        POLU TECH Integrated
                                        Technology
                                        <br />
                                        Secure Digital Access
                                    </Typography>
                                </Box>
                            </Paper>
                        </motion.div>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Login;






// import React, { useContext, useState } from "react";
// import { useNavigate, Link } from "react-router-dom";
// import Swal from "sweetalert2";
// import { motion } from "framer-motion";

// import {
//     Box,
//     Typography,
//     Paper,
//     TextField,
//     InputAdornment,
//     IconButton,
//     Button,
//     CircularProgress,
//     useMediaQuery,
// } from "@mui/material";

// import {
//     School,
//     Email,
//     Lock,
//     Visibility,
//     VisibilityOff,
//     ArrowBack,
//     Security,
//     AutoAwesome,
//     Bolt,
//     VerifiedUser,
// } from "@mui/icons-material";

// import api from "../api/api";
// import { AuthContext } from "../AuthContext";

// import student from "./schoolmanagementsystem.png";

// const COLORS = {
//     skyBlue: "#EAF8FF",
//     lightBlue: "#DDF3FF",
//     softBlue: "#C7ECFF",
//     oceanBlue: "#087EA4",
//     deepOcean: "#045A78",
//     deepBlue: "#031B4E",
//     navy: "#020617",
//     black: "#101820",
//     white: "#FFFFFF",
//     orange: "#F97316",
//     deepOrange: "#D94801",
//     softOrange: "#FFF1E8",
//     text: "#102A36",
//     muted: "#607D8B",
//     border: "#D7EAF2",
// };

// const EnergyParticle = ({
//     top,
//     left,
//     right,
//     bottom,
//     size = 7,
//     delay = 0,
//     color = COLORS.white,
// }) => (
//     <motion.div
//         initial={{
//             opacity: 0,
//             scale: 0.2,
//         }}
//         animate={{
//             opacity: [0, 1, 0.7, 0],
//             scale: [0.2, 1, 1.25, 0.1],
//             x: [0, -10, 12, -20],
//             y: [0, -12, -26, -42],
//         }}
//         transition={{
//             duration: 3.5,
//             delay,
//             repeat: Infinity,
//             ease: "easeOut",
//         }}
//         style={{
//             position: "absolute",
//             top,
//             left,
//             right,
//             bottom,
//             width: size,
//             height: size,
//             borderRadius: "50%",
//             background: color,
//             boxShadow: `0 0 18px ${color}`,
//             zIndex: 12,
//         }}
//     />
// );

// const SpeedLine = ({
//     top,
//     left,
//     right,
//     width = 120,
//     rotate = -20,
//     delay = 0,
//     color = COLORS.white,
// }) => (
//     <motion.div
//         initial={{
//             opacity: 0,
//             x: 0,
//             scaleX: 0.3,
//         }}
//         animate={{
//             opacity: [0, 0.9, 0],
//             x: [0, 35, 75],
//             scaleX: [0.3, 1, 0.5],
//         }}
//         transition={{
//             duration: 2.2,
//             delay,
//             repeat: Infinity,
//             ease: "easeOut",
//         }}
//         style={{
//             position: "absolute",
//             top,
//             left,
//             right,
//             width,
//             height: 5,
//             borderRadius: 20,
//             background: `linear-gradient(90deg, transparent, ${color})`,
//             transform: `rotate(${rotate}deg)`,
//             transformOrigin: "left center",
//             zIndex: 8,
//         }}
//     />
// );

// const OrbitEnergy = ({
//     size,
//     duration,
//     reverse = false,
//     color,
//     secondaryColor,
//     delay = 0,
//     dashed = false,
// }) => (
//     <motion.div
//         animate={{
//             rotate: reverse ? -360 : 360,
//         }}
//         transition={{
//             duration,
//             delay,
//             repeat: Infinity,
//             ease: "linear",
//         }}
//         style={{
//             position: "absolute",
//             width: size,
//             height: size,
//             borderRadius: "50%",
//             border: dashed
//                 ? `2px dashed ${color}`
//                 : `2px solid ${color}`,
//             borderTopColor: secondaryColor || color,
//             borderRightColor: secondaryColor || color,
//             zIndex: 7,
//             pointerEvents: "none",
//         }}
//     >
//         <motion.div
//             animate={{
//                 scale: [0.75, 1.25, 0.75],
//                 opacity: [0.55, 1, 0.55],
//             }}
//             transition={{
//                 duration: 1.5,
//                 repeat: Infinity,
//                 ease: "easeInOut",
//             }}
//             style={{
//                 position: "absolute",
//                 top: -7,
//                 left: "50%",
//                 transform: "translateX(-50%)",
//                 width: 13,
//                 height: 13,
//                 borderRadius: "50%",
//                 background: secondaryColor || color,
//                 boxShadow: `0 0 20px ${secondaryColor || color}`,
//             }}
//         />

//         <Box
//             sx={{
//                 position: "absolute",
//                 right: -6,
//                 top: "50%",
//                 width: 10,
//                 height: 10,
//                 borderRadius: "50%",
//                 background: color,
//                 boxShadow: `0 0 16px ${color}`,
//                 transform: "translateY(-50%)",
//             }}
//         />

//         <Box
//             sx={{
//                 position: "absolute",
//                 bottom: -5,
//                 left: "20%",
//                 width: 8,
//                 height: 8,
//                 borderRadius: "50%",
//                 background: secondaryColor || color,
//                 boxShadow: `0 0 15px ${secondaryColor || color}`,
//             }}
//         />
//     </motion.div>
// );

// const Login = () => {
//     const navigate = useNavigate();
//     const { setUser } = useContext(AuthContext);

//     const isMobile = useMediaQuery("(max-width:900px)");

//     const [email, setEmail] = useState("");
//     const [password, setPassword] = useState("");
//     const [loading, setLoading] = useState(false);
//     const [showPassword, setShowPassword] = useState(false);

//     const redirectUser = (user) => {
//         const role = String(
//             user?.user_type || user?.role || ""
//         )
//             .toLowerCase()
//             .trim();

//         switch (role) {
//             case "owner":
//                 navigate("/owner-dashboard");
//                 break;

//             case "admin":
//                 navigate("/newAdminDashboard");
//                 break;

//             case "teacher":
//                 navigate("/teacher-dashboard");
//                 break;

//             case "student":
//                 navigate("/student-dashboard");
//                 break;

//             case "parent":
//                 navigate("/parentsDashboard");
//                 break;

//             case "registry":
//                 navigate("/registerDashboard");
//                 break;

//             case "principal":
//                 navigate("/principal-dashboard");
//                 break;

//             case "proprietor":
//                 navigate("/proprietor-dashboard");
//                 break;

//             case "vpi":
//                 navigate("/vpi-dashboard");
//                 break;

//             case "vpa":
//                 navigate("/newVpaDashboard");
//                 break;

//             case "it personel":
//             case "it personnel":
//                 navigate("/it-dashboard");
//                 break;

//             case "business_manager":
//             case "business manager":
//                 navigate("/businessManagerDashboard");
//                 break;

//             case "admission officer":
//             case "admission_officer":
//                 navigate("/admissionOfficialDashboard");
//                 break;

//             case "dean":
//                 navigate("/dean-dashboard");
//                 break;

//             default:
//                 navigate("/");
//         }
//     };

//     const clearAuthenticationData = () => {
//         localStorage.removeItem("access");
//         localStorage.removeItem("refresh");
//         localStorage.removeItem("user");
//         localStorage.removeItem("employment_status");
//         localStorage.removeItem("employment_status_display");
//         localStorage.removeItem("employment_academic_year");
//         localStorage.removeItem("auth_email");
//     };

//     const getLoginError = (error) => {
//         const responseData = error?.response?.data;

//         if (responseData?.detail) {
//             return String(responseData.detail);
//         }

//         if (responseData?.message) {
//             return String(responseData.message);
//         }

//         if (typeof responseData === "string") {
//             return responseData;
//         }

//         if (
//             responseData &&
//             typeof responseData === "object"
//         ) {
//             const messages = [];

//             Object.entries(responseData).forEach(
//                 ([field, value]) => {
//                     if (field === "detail") {
//                         return;
//                     }

//                     if (Array.isArray(value)) {
//                         value.forEach((item) => {
//                             if (
//                                 item !== null &&
//                                 item !== undefined
//                             ) {
//                                 messages.push(String(item));
//                             }
//                         });
//                     } else if (
//                         typeof value === "string"
//                     ) {
//                         messages.push(value);
//                     }
//                 }
//             );

//             if (messages.length) {
//                 return messages.join("\n");
//             }
//         }

//         return "Invalid email or password.";
//     };

//     const showEmploymentStatusAlert = (data) => {
//         const errorCode = String(
//             data?.error_code || ""
//         )
//             .toUpperCase()
//             .trim();

//         const status = String(
//             data?.employment_status || ""
//         )
//             .toLowerCase()
//             .trim();

//         const academicYear =
//             data?.academic_year ||
//             data?.academic_year_name ||
//             "the current academic year";

//         const statusDisplay =
//             data?.employment_status_display ||
//             (status === "fired"
//                 ? "SACKED / FIRED"
//                 : status === "removed"
//                     ? "REMOVED"
//                     : "");

//         if (
//             errorCode === "TEACHER_FIRED" ||
//             errorCode === "EMPLOYMENT_FIRED" ||
//             status === "fired"
//         ) {
//             Swal.fire({
//                 icon: "error",
//                 title: "Account Blocked!",
//                 html: `
//                     <div style="text-align:center;">
//                         <p style="font-size:16px;line-height:1.6;">
//                             Your account cannot be used to access
//                             the school system.
//                         </p>

//                         <div style="
//                             display:inline-block;
//                             padding:8px 16px;
//                             border-radius:20px;
//                             background:#FEF2F2;
//                             color:#B91C1C;
//                             font-weight:800;
//                             margin:10px 0 14px;
//                         ">
//                             EMPLOYMENT STATUS:
//                             ${statusDisplay || "SACKED / FIRED"}
//                         </div>

//                         <p style="
//                             color:#6B7280;
//                             line-height:1.6;
//                         ">
//                             Your most recent employment record
//                             indicates that your employment was
//                             terminated.
//                         </p>

//                         <p style="
//                             color:#374151;
//                             font-weight:600;
//                             line-height:1.6;
//                         ">
//                             Please contact an authorized school
//                             administrator for assistance.
//                         </p>

//                         <p style="
//                             color:#6B7280;
//                             font-size:13px;
//                             margin-top:12px;
//                         ">
//                             Latest employment record:
//                             <strong>${academicYear}</strong>
//                         </p>
//                     </div>
//                 `,
//                 confirmButtonText: "Understood",
//                 confirmButtonColor: COLORS.orange,
//             });

//             return true;
//         }

//         if (
//             errorCode === "TEACHER_REMOVED" ||
//             errorCode === "EMPLOYMENT_REMOVED" ||
//             status === "removed"
//         ) {
//             Swal.fire({
//                 icon: "warning",
//                 title: "Account Blocked !",
//                 html: `
//                     <div style="text-align:center;">
//                         <p style="font-size:16px;line-height:1.6;">
//                             Your account cannot be used to access
//                             the school system.
//                         </p>

//                         <div style="
//                             display:inline-block;
//                             padding:8px 16px;
//                             border-radius:20px;
//                             background:#FFF7ED;
//                             color:#C2410C;
//                             font-weight:800;
//                             margin:10px 0 14px;
//                         ">
//                             EMPLOYMENT STATUS:
//                             ${statusDisplay || "REMOVED"}
//                         </div>

//                         <p style="
//                             color:#6B7280;
//                             line-height:1.6;
//                         ">
//                             Your most recent employment record
//                             indicates that you have been removed
//                             from the school.
//                         </p>

//                         <p style="
//                             color:#374151;
//                             font-weight:600;
//                             line-height:1.6;
//                         ">
//                             Please contact an authorized school
//                             administrator for assistance.
//                         </p>

//                         <p style="
//                             color:#6B7280;
//                             font-size:13px;
//                             margin-top:12px;
//                         ">
//                             Latest employment record:
//                             <strong>${academicYear}</strong>
//                         </p>
//                     </div>
//                 `,
//                 confirmButtonText: "Understood",
//                 confirmButtonColor: COLORS.orange,
//             });

//             return true;
//         }

//         if (errorCode === "TEACHER_NOT_ASSIGNED") {
//             Swal.fire({
//                 icon: "warning",
//                 title: "Teacher Not Assigned",
//                 html: `
//                     <div style="text-align:center;">
//                         <p style="font-size:16px;line-height:1.6;">
//                             Your teacher account is not currently
//                             assigned to an active academic year.
//                         </p>

//                         <p style="
//                             font-size:17px;
//                             font-weight:800;
//                             color:${COLORS.oceanBlue};
//                         ">
//                             ${academicYear}
//                         </p>

//                         <p style="color:#6B7280;line-height:1.6;">
//                             Please contact the school administration
//                             to have your teacher record assigned.
//                         </p>
//                     </div>
//                 `,
//                 confirmButtonText: "Understood",
//                 confirmButtonColor: COLORS.orange,
//             });

//             return true;
//         }

//         if (errorCode === "NO_ACTIVE_ACADEMIC_YEAR") {
//             Swal.fire({
//                 icon: "warning",
//                 title: "No Active Academic Year",
//                 text:
//                     "There is currently no active academic year. Please contact the school administration.",
//                 confirmButtonText: "Understood",
//                 confirmButtonColor: COLORS.orange,
//             });

//             return true;
//         }

//         if (
//             errorCode === "EMPLOYMENT_LOGIN_BLOCKED" ||
//             errorCode === "LOGIN_BLOCKED"
//         ) {
//             Swal.fire({
//                 icon: "warning",
//                 title: "Login Blocked",
//                 html: `
//                     <div style="text-align:center;">
//                         <p style="font-size:16px;line-height:1.6;">
//                             Your account is currently blocked from
//                             accessing the school management system.
//                         </p>

//                         <p style="
//                             color:#374151;
//                             font-weight:600;
//                             line-height:1.6;
//                         ">
//                             Please contact an authorized school
//                             administrator.
//                         </p>
//                     </div>
//                 `,
//                 confirmButtonText: "Understood",
//                 confirmButtonColor: COLORS.orange,
//             });

//             return true;
//         }

//         return false;
//     };

//     const handleBlockedResponse = (data) => {
//         if (!data) {
//             return false;
//         }

//         const status = String(
//             data?.employment_status || ""
//         )
//             .toLowerCase()
//             .trim();

//         const errorCode = String(
//             data?.error_code || ""
//         )
//             .toUpperCase()
//             .trim();

//         const employmentCodes = [
//             "TEACHER_REMOVED",
//             "TEACHER_FIRED",
//             "EMPLOYMENT_REMOVED",
//             "EMPLOYMENT_FIRED",
//             "EMPLOYMENT_LOGIN_BLOCKED",
//             "LOGIN_BLOCKED",
//             "TEACHER_NOT_ASSIGNED",
//             "NO_ACTIVE_ACADEMIC_YEAR",
//         ];

//         const isEmploymentError =
//             employmentCodes.includes(errorCode) ||
//             ["removed", "fired"].includes(status);

//         if (!isEmploymentError) {
//             return false;
//         }

//         clearAuthenticationData();
//         return showEmploymentStatusAlert(data);
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();

//         const cleanEmail = email.trim();

//         if (!cleanEmail || !password) {
//             Swal.fire({
//                 icon: "warning",
//                 title: "Missing Information",
//                 text:
//                     "Please enter your email address and password.",
//                 confirmButtonColor: COLORS.orange,
//             });

//             return;
//         }

//         setLoading(true);

//         try {
//             clearAuthenticationData();

//             const response = await api.post(
//                 "/api/token/",
//                 {
//                     email: cleanEmail,
//                     password,
//                 }
//             );

//             console.log(
//                 "TOKEN RESPONSE:",
//                 response.data
//             );

//             if (
//                 handleBlockedResponse(
//                     response.data
//                 )
//             ) {
//                 return;
//             }

//             const role = String(
//                 response.data?.user_type ||
//                 response.data?.user?.role ||
//                 ""
//             )
//                 .toLowerCase()
//                 .trim();

//             const authenticationRoles = [
//                 "admin",
//                 "registry",
//                 "principal",
//                 "proprietor",
//                 "business_manager",
//                 "business manager",
//                 "vpi",
//                 "vpa",
//                 "it personel",
//                 "it personnel",
//             ];

//             if (
//                 response.data?.requires_authentication &&
//                 authenticationRoles.includes(role)
//             ) {
//                 const authEmail =
//                     response.data?.email ||
//                     response.data?.user?.email ||
//                     cleanEmail;

//                 localStorage.setItem(
//                     "auth_email",
//                     authEmail
//                 );

//                 navigate(
//                     "/loginAuthentication",
//                     {
//                         state: {
//                             email: authEmail,
//                         },
//                     }
//                 );

//                 return;
//             }

//             const access =
//                 response.data?.access;

//             const refresh =
//                 response.data?.refresh;

//             if (!access || !refresh) {
//                 throw new Error(
//                     "Authentication tokens were not returned."
//                 );
//             }

//             localStorage.setItem(
//                 "access",
//                 access
//             );

//             localStorage.setItem(
//                 "refresh",
//                 refresh
//             );

//             if (
//                 response.data?.employment_status
//             ) {
//                 localStorage.setItem(
//                     "employment_status",
//                     response.data.employment_status
//                 );
//             }

//             if (
//                 response.data
//                     ?.employment_status_display
//             ) {
//                 localStorage.setItem(
//                     "employment_status_display",
//                     response.data
//                         .employment_status_display
//                 );
//             }

//             if (
//                 response.data?.academic_year
//             ) {
//                 localStorage.setItem(
//                     "employment_academic_year",
//                     response.data.academic_year
//                 );
//             }

//             window.dispatchEvent(
//                 new Event("user-logged-in")
//             );

//             let profileResponse;

//             try {
//                 profileResponse =
//                     await api.get(
//                         "/api/user-profile/"
//                     );
//             } catch (profileError) {
//                 clearAuthenticationData();
//                 throw profileError;
//             }

//             const user =
//                 profileResponse.data;

//             const frontendStatus = String(
//                 response.data
//                     ?.employment_status ||
//                 user?.employment_status ||
//                 ""
//             )
//                 .toLowerCase()
//                 .trim();

//             if (
//                 frontendStatus === "removed" ||
//                 frontendStatus === "fired"
//             ) {
//                 clearAuthenticationData();

//                 showEmploymentStatusAlert({
//                     error_code:
//                         frontendStatus === "removed"
//                             ? "TEACHER_REMOVED"
//                             : "TEACHER_FIRED",
//                     employment_status:
//                         frontendStatus,
//                     employment_status_display:
//                         frontendStatus === "removed"
//                             ? "REMOVED"
//                             : "SACKED / FIRED",
//                     academic_year:
//                         response.data?.academic_year ||
//                         response.data
//                             ?.academic_year_name,
//                 });

//                 return;
//             }

//             setUser(user);

//             localStorage.setItem(
//                 "user",
//                 JSON.stringify(user)
//             );

//             Swal.fire({
//                 icon: "success",
//                 title: "Welcome Back!",
//                 text: `Welcome ${
//                     user?.user_type ||
//                     user?.role ||
//                     ""
//                 }`,
//                 timer: 1800,
//                 showConfirmButton: false,
//                 background: COLORS.white,
//                 color: COLORS.black,
//                 iconColor: COLORS.orange,
//             });

//             setTimeout(() => {
//                 redirectUser(user);
//             }, 1700);
//         } catch (error) {
//             console.error(
//                 "LOGIN ERROR:",
//                 error
//             );

//             const responseData =
//                 error?.response?.data;

//             if (responseData) {
//                 const handled =
//                     handleBlockedResponse(
//                         responseData
//                     );

//                 if (handled) {
//                     return;
//                 }
//             }

//             Swal.fire({
//                 icon: "error",
//                 title: "Login Failed",
//                 text: getLoginError(error),
//                 confirmButtonText: "Try Again",
//                 confirmButtonColor: COLORS.orange,
//                 background: COLORS.white,
//                 color: COLORS.black,
//             });
//         } finally {
//             setLoading(false);
//         }
//     };

//     return (
//         <Box
//             sx={{
//                 width: "100%",
//                 minHeight: "100dvh",
//                 overflow: "hidden",
//                 background: COLORS.skyBlue,
//             }}
//         >
//             <Box
//                 sx={{
//                     width: "100%",
//                     minHeight: "100dvh",
//                     display: "flex",
//                     flexDirection: {
//                         xs: "column",
//                         md: "row",
//                     },
//                 }}
//             >
//                 {/* DESKTOP LEFT PANEL */}
//                 <Box
//                     sx={{
//                         display: {
//                             xs: "none",
//                             md: "flex",
//                         },
//                         position: "relative",
//                         width: "53%",
//                         height: "100dvh",
//                         flexDirection: "column",
//                         overflow: "hidden",
//                         color: COLORS.white,
//                         background:
//                              "linear-gradient(145deg, #020617 0%, #010b22 38%, #011328 72%, #001f30 100%)",
//                             // "linear-gradient(145deg, #020617 0%, #031B4E 38%, #063B78 72%, #075985 100%)",
//                     }}
//                 >
//                     <motion.div
//                         animate={{
//                             scale: [1, 1.2, 1],
//                             opacity: [0.12, 0.3, 0.12],
//                         }}
//                         transition={{
//                             duration: 8,
//                             repeat: Infinity,
//                             ease: "easeInOut",
//                         }}
//                         style={{
//                             position: "absolute",
//                             width: 650,
//                             height: 650,
//                             borderRadius: "50%",
//                             top: -360,
//                             left: -320,
//                             background:
//                                 "radial-gradient(circle, rgba(8,126,164,.55), transparent 68%)",
//                         }}
//                     />

//                     <motion.div
//                         animate={{
//                             scale: [1, 1.22, 1],
//                             opacity: [0.1, 0.25, 0.1],
//                         }}
//                         transition={{
//                             duration: 9,
//                             repeat: Infinity,
//                             ease: "easeInOut",
//                         }}
//                         style={{
//                             position: "absolute",
//                             width: 600,
//                             height: 600,
//                             borderRadius: "50%",
//                             right: -320,
//                             bottom: -320,
//                             background:
//                                 "radial-gradient(circle, rgba(249,115,22,.3), transparent 68%)",
//                         }}
//                     />

//                     <Box
//                         sx={{
//                             position: "relative",
//                             zIndex: 20,
//                             textAlign: "center",
//                             pt: 5,
//                             px: 4,
//                         }}
//                     >
// <Box
//     sx={{
//         display: "inline-flex",
//         alignItems: "center",
//         gap: 1.5,
//         px: 2.2,
//         py: 1,
//         borderRadius: "50px",
//         background: "rgba(255,255,255,.08)",
//         border: "1px solid rgba(255,255,255,.2)",
//         backdropFilter: "blur(12px)",
//     }}
// >
//     <School
//         sx={{
//             fontSize: 24,
//             color: COLORS.white,
//         }}
//     />

//     <Typography
//         sx={{
//             fontSize: ".72rem",
//             fontWeight: 900,
//             letterSpacing: 1.5,
//             color: COLORS.white,
//         }}
//     >
//         <Box
//             component="span"
//             sx={{
//                 color: "#D35400",
//                 fontWeight: 1000,
//             }}
//         >
//             POLU
//         </Box>{" "}
//         SCHOOL MANAGEMENT PORTAL
//     </Typography>
// </Box>

//                         <Typography
//                             sx={{
//                                 mt: 2.5,
//                                 fontSize: {
//                                     md: "1.65rem",
//                                     lg: "2rem",
//                                 },
//                                 fontWeight: 950,
//                                 letterSpacing: 1.5,
//                                 lineHeight: 1.15,
//                                 textTransform: "uppercase",
//                             }}
//                         >
//                             Salvation In Christ
//                         </Typography>

//                         <Typography
//                             sx={{
//                                 mt: 0.7,
//                                 color: "#BDEEFF",
//                                 fontSize: {
//                                     md: ".9rem",
//                                     lg: "1rem",
//                                 },
//                                 fontWeight: 800,
//                                 letterSpacing: 2,
//                             }}
//                         >
//                             SCHOOL SYSTEM
//                         </Typography>

//                         <Box
//                             sx={{
//                                 width: 90,
//                                 height: 4,
//                                 borderRadius: 20,
//                                 mx: "auto",
//                                 mt: 2,
//                                 background:
//                                     "linear-gradient(90deg, #FFFFFF, #F97316)",
//                             }}
//                         />
//                     </Box>

//                     {/* STUDENT ENERGY AREA */}
//                     <Box
//                         sx={{
//                             position: "relative",
//                             flex: 1,
//                             minHeight: 0,
//                             display: "flex",
//                             alignItems: "center",
//                             justifyContent: "center",
//                         }}
//                     >
//                         {/* Clearly moving orbital energy */}
//                         <OrbitEnergy
//                             size={500}
//                             duration={17}
//                             color="rgba(255,255,255,.25)"
//                             secondaryColor={COLORS.orange}
//                         />

//                         <OrbitEnergy
//                             size={420}
//                             duration={11}
//                             reverse
//                             color="rgba(8,126,164,.75)"
//                             secondaryColor="#FFFFFF"
//                             dashed
//                         />

//                         <OrbitEnergy
//                             size={350}
//                             duration={8}
//                             color="rgba(249,115,22,.8)"
//                             secondaryColor="#FFFFFF"
//                         />

//                         <OrbitEnergy
//                             size={280}
//                             duration={6}
//                             reverse
//                             color="rgba(255,255,255,.3)"
//                             secondaryColor={COLORS.orange}
//                             dashed
//                         />

//                         {/* Additional moving particles */}
//                         <EnergyParticle
//                             top="24%"
//                             left="27%"
//                             size={9}
//                             delay={0}
//                         />

//                         <EnergyParticle
//                             top="31%"
//                             right="26%"
//                             size={7}
//                             delay={0.7}
//                             color={COLORS.orange}
//                         />

//                         <EnergyParticle
//                             top="43%"
//                             left="16%"
//                             size={6}
//                             delay={1.3}
//                         />

//                         <EnergyParticle
//                             top="49%"
//                             right="16%"
//                             size={9}
//                             delay={1.9}
//                         />

//                         <EnergyParticle
//                             top="60%"
//                             left="24%"
//                             size={7}
//                             delay={2.5}
//                             color={COLORS.orange}
//                         />

//                         <EnergyParticle
//                             top="57%"
//                             right="23%"
//                             size={6}
//                             delay={3}
//                         />

//                         {/* Moving speed streaks */}
//                         <SpeedLine
//                             top="28%"
//                             left="8%"
//                             width={170}
//                             rotate={-22}
//                             delay={0}
//                         />

//                         <SpeedLine
//                             top="34%"
//                             left="4%"
//                             width={110}
//                             rotate={-19}
//                             delay={0.6}
//                             color={COLORS.orange}
//                         />

//                         <SpeedLine
//                             top="42%"
//                             right="5%"
//                             width={155}
//                             rotate={-26}
//                             delay={1.1}
//                         />

//                         <SpeedLine
//                             top="49%"
//                             right="8%"
//                             width={100}
//                             rotate={-24}
//                             delay={1.7}
//                             color={COLORS.orange}
//                         />

//                         <SpeedLine
//                             top="60%"
//                             left="9%"
//                             width={125}
//                             rotate={-16}
//                             delay={2.1}
//                         />

//                         <SpeedLine
//                             top="64%"
//                             right="10%"
//                             width={130}
//                             rotate={-20}
//                             delay={2.5}
//                         />

//                         {/* Static glow behind student */}
//                         <Box
//                             sx={{
//                                 position: "absolute",
//                                 width: 420,
//                                 height: 420,
//                                 borderRadius: "50%",
//                                 background:
//                                     "radial-gradient(circle, rgba(8,126,164,.3), rgba(3,27,78,.05) 55%, transparent 72%)",
//                                 zIndex: 4,
//                             }}
//                         />

//                         {/* STATIC STUDENT - NO MOTION */}
//                         <Box
//                             component="img"
//                             src={student}
//                             alt="Excited student"
//                             sx={{
//                                 position: "relative",
//                                 zIndex: 20,
//                                 width: {
//                                     md: "74%",
//                                     lg: "78%",
//                                 },
//                                 maxWidth: 700,
//                                 height: "82%",
//                                 objectFit: "contain",
//                                 objectPosition: "center bottom",
//                                 filter:
//                                     "drop-shadow(0 30px 35px rgba(0,0,0,.45))",
//                                 userSelect: "none",
//                                 pointerEvents: "none",
//                             }}
//                         />

//                         <motion.div
//                             animate={{
//                                 scale: [0.8, 1.2, 0.8],
//                                 rotate: [-10, 8, -10],
//                                 opacity: [0.4, 1, 0.4],
//                             }}
//                             transition={{
//                                 duration: 2.2,
//                                 repeat: Infinity,
//                             }}
//                             style={{
//                                 position: "absolute",
//                                 right: "20%",
//                                 top: "28%",
//                                 zIndex: 25,
//                             }}
//                         >
//                             <Bolt
//                                 sx={{
//                                     fontSize: 42,
//                                     color: COLORS.white,
//                                     filter:
//                                         "drop-shadow(0 0 14px rgba(255,255,255,.8))",
//                                 }}
//                             />
//                         </motion.div>

//                         <motion.div
//                             animate={{
//                                 scale: [0.7, 1.3, 0.7],
//                                 rotate: [0, 180, 360],
//                                 opacity: [0.35, 1, 0.35],
//                             }}
//                             transition={{
//                                 duration: 4,
//                                 repeat: Infinity,
//                                 ease: "easeInOut",
//                             }}
//                             style={{
//                                 position: "absolute",
//                                 left: "20%",
//                                 top: "38%",
//                                 zIndex: 25,
//                             }}
//                         >
//                             <AutoAwesome
//                                 sx={{
//                                     fontSize: 34,
//                                     color: COLORS.orange,
//                                 }}
//                             />
//                         </motion.div>
//                     </Box>

//                     <Box
//                         sx={{
//                             position: "relative",
//                             zIndex: 20,
//                             textAlign: "center",
//                             pb: 3,
//                         }}
//                     >
//                         <Typography
//                             sx={{
//                                 color:
//                                     "rgba(255,255,255,.7)",
//                                 fontSize: ".68rem",
//                                 fontWeight: 800,
//                                 letterSpacing: 1.4,
//                             }}
//                         >
//                             LEARNING • CHARACTER • EXCELLENCE
//                         </Typography>
//                     </Box>
//                 </Box>

//                 {/* MOBILE BRAND */}
//                 <Box
//                     sx={{
//                         display: {
//                             xs: "flex",
//                             md: "none",
//                         },
//                         position: "fixed",
//                         top: 0,
//                         left: 0,
//                         right: 0,
//                         zIndex: 30,
//                         justifyContent: "center",
//                         pt: 2,
//                         pointerEvents: "none",
//                     }}
//                 >
//                     <Box
//                         sx={{
//                             display: "flex",
//                             alignItems: "center",
//                             gap: 1,
//                             px: 2,
//                             py: 0.9,
//                             borderRadius: "50px",
//                             background:
//                                 "rgba(255,255,255,.94)",
//                             border:
//                                 `1px solid ${COLORS.border}`,
//                             boxShadow:
//                                 "0 8px 25px rgba(4,90,120,.1)",
//                         }}
//                     >
//                         <School
//                             sx={{
//                                 fontSize: 21,
//                                 color: COLORS.oceanBlue,
//                             }}
//                         />

//                         <Typography
//                             sx={{
//                                 fontSize: ".68rem",
//                                 fontWeight: 900,
//                                 color: COLORS.deepOcean,
//                                 letterSpacing: 0.7,
//                             }}
//                         >
//                             SALVATION IN CHRIST
//                         </Typography>
//                     </Box>
//                 </Box>

//                 {/* LOGIN AREA */}
//                 <Box
//                     sx={{
//                         position: "relative",
//                         width: {
//                             xs: "100%",
//                             md: "47%",
//                         },
//                         minHeight: "100dvh",
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                         boxSizing: "border-box",
//                         overflowY: "auto",
//                         overflowX: "hidden",
//                         px: {
//                             xs: 1.5,
//                             sm: 3,
//                             md: 5,
//                         },
//                         py: {
//                             xs: 6,
//                             sm: 5,
//                             md: 4,
//                         },
//                         background:
//                             "linear-gradient(135deg, #FFFFFF 0%, #F3FBFE 55%, #E4F7FF 100%)",
//                     }}
//                 >
//                     {/* BACKGROUND DECORATION */}
//                     <motion.div
//                         animate={{
//                             scale: [1, 1.2, 1],
//                             opacity: [0.12, 0.23, 0.12],
//                         }}
//                         transition={{
//                             duration: 7,
//                             repeat: Infinity,
//                             ease: "easeInOut",
//                         }}
//                         style={{
//                             position: "absolute",
//                             width: 400,
//                             height: 400,
//                             borderRadius: "50%",
//                             background:
//                                 "radial-gradient(circle, rgba(8,126,164,.25), transparent 68%)",
//                             top: -230,
//                             right: -210,
//                         }}
//                     />

//                     <motion.div
//                         animate={{
//                             scale: [0.9, 1.15, 0.9],
//                             opacity: [0.08, 0.2, 0.08],
//                         }}
//                         transition={{
//                             duration: 8,
//                             repeat: Infinity,
//                             ease: "easeInOut",
//                         }}
//                         style={{
//                             position: "absolute",
//                             width: 350,
//                             height: 350,
//                             borderRadius: "50%",
//                             background:
//                                 "radial-gradient(circle, rgba(249,115,22,.2), transparent 68%)",
//                             bottom: -200,
//                             left: -200,
//                         }}
//                     />

//                     {/* LOGIN CARD */}
//                     <Box
//                         sx={{
//                             position: "relative",
//                             zIndex: 10,
//                             width: "100%",
//                             maxWidth: 460,
//                             mx: "auto",
//                         }}
//                     >
//                         <motion.div
//                             initial={{
//                                 opacity: 0,
//                                 y: 25,
//                             }}
//                             animate={{
//                                 opacity: 1,
//                                 y: 0,
//                             }}
//                             transition={{
//                                 duration: 0.65,
//                                 ease: "easeOut",
//                             }}
//                         >
//                             <Paper
//                                 elevation={0}
//                                 sx={{
//                                     position: "relative",
//                                     width: "100%",
//                                     boxSizing: "border-box",
//                                     overflow: "hidden",
//                                     p: {
//                                         xs: 2.4,
//                                         sm: 3.5,
//                                         md: 4,
//                                     },
//                                     borderRadius: {
//                                         xs: "20px",
//                                         sm: "26px",
//                                         md: "30px",
//                                     },
//                                     background:
//                                         "rgba(255,255,255,.98)",
//                                     border:
//                                         `1px solid ${COLORS.border}`,
//                                     boxShadow:
//                                         "0 25px 65px rgba(4,90,120,.14)",
//                                 }}
//                             >
//                                 <Box
//                                     sx={{
//                                         position: "absolute",
//                                         top: 0,
//                                         left: 0,
//                                         right: 0,
//                                         height: 5,
//                                         background:
//                                             `linear-gradient(90deg, ${COLORS.deepBlue}, ${COLORS.oceanBlue}, ${COLORS.orange})`,
//                                     }}
//                                 />

//                                 {/* HEADER */}
//                                 <Box
//                                     sx={{
//                                         textAlign: "center",
//                                         mb: 3,
//                                     }}
//                                 >
//                                     {/* <Box
//                                         sx={{
//                                             width: 64,
//                                             height: 64,
//                                             mx: "auto",
//                                             borderRadius: "20px",
//                                             display: "flex",
//                                             alignItems: "center",
//                                             justifyContent: "center",
//                                             background:
//                                                 `linear-gradient(135deg, ${COLORS.deepBlue}, ${COLORS.oceanBlue})`,
//                                             boxShadow:
//                                                 "0 13px 30px rgba(4,90,120,.22)",
//                                             position: "relative",
//                                         }}
//                                     >
//                                         <Lock
//                                             sx={{
//                                                 fontSize: 30,
//                                                 color: COLORS.white,
//                                             }}
//                                         />

//                                         <Box
//                                             sx={{
//                                                 position: "absolute",
//                                                 width: 10,
//                                                 height: 10,
//                                                 borderRadius: "50%",
//                                                 background:
//                                                     COLORS.orange,
//                                                 right: -3,
//                                                 top: -3,
//                                                 border:
//                                                     `3px solid ${COLORS.white}`,
//                                             }}
//                                         />
//                                     </Box> */}
//                                     <Box
//     sx={{
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         gap: 1.25,
//         width: "100%",
//         whiteSpace: "nowrap",
//     }}
// >
//     <Box
//         sx={{
//             width: 56,
//             height: 56,
//             flexShrink: 0,
//             borderRadius: "17px",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             background: `linear-gradient(
//                 135deg,
//                 ${COLORS.deepBlue},
//                 ${COLORS.oceanBlue}
//             )`,
//             boxShadow: "0 10px 25px rgba(0, 31, 91, 0.20)",
//             position: "relative",
//         }}
//     >
//         <Lock
//             sx={{
//                 fontSize: 27,
//                 color: COLORS.white,
//             }}
//         />

//         <Box
//             sx={{
//                 position: "absolute",
//                 width: 9,
//                 height: 9,
//                 borderRadius: "50%",
//                 background: "#e85d04",
//                 right: -2,
//                 top: -2,
//                 border: `3px solid ${COLORS.white}`,
//             }}
//         />
//     </Box>

//     <Typography
//         component="div"
//         sx={{
//             display: "flex",
//             alignItems: "baseline",
//             gap: 0.65,
//             whiteSpace: "nowrap",
//             fontFamily:
//                 '"Arial Black", "Montserrat", "Segoe UI", sans-serif',
//             fontWeight: 900,
//             letterSpacing: "-0.25px",
//             fontSize: {
//                 xs: "0.78rem",
//                 sm: "0.95rem",
//                 md: "1.02rem",
//             },
//         }}
//     >
//         <Box
//             component="span"
//             sx={{
//                 color: "#e85d04",
//                 fontWeight: 950,
//                 letterSpacing: "0.2px",
//             }}
//         >
//             POLU
//         </Box>

//         <Box
//             component="span"
//             sx={{
//                 color: COLORS.black,
//                 fontWeight: 800,
//                 letterSpacing: "0.35px",
//             }}
//         >
//             SCHOOL MANAGEMENT
//         </Box>
//     </Typography>
// </Box>

//                                     <Typography
//                                         sx={{
//                                             mt: 2,
//                                             fontSize: {
//                                                 xs: "1.55rem",
//                                                 sm: "1.85rem",
//                                             },
//                                             fontWeight: 950,
//                                             color: COLORS.text,
//                                         }}
//                                     >
//                                         Welcome Back
//                                     </Typography>

//                                     <Typography
//                                         sx={{
//                                             color: COLORS.muted,
//                                             mt: 0.8,
//                                             lineHeight: 1.65,
//                                             fontSize: ".87rem",
//                                         }}
//                                     >
//                                         Sign in to securely access your
//                                         <br />

//                                         <strong
//                                             style={{
//                                                 color:
//                                                     COLORS.oceanBlue,
//                                             }}
//                                         >
//                                             Salvation In Christ School System
//                                         </strong>
//                                     </Typography>
//                                 </Box>

//                                 {/* SECURITY BADGE */}
//                                 <Box
//                                     sx={{
//                                         display: "flex",
//                                         alignItems: "center",
//                                         justifyContent: "center",
//                                         gap: 0.8,
//                                         mb: 2.5,
//                                         py: 0.9,
//                                         px: 1.5,
//                                         borderRadius: "50px",
//                                         background:
//                                             COLORS.softBlue,
//                                         border:
//                                             "1px solid rgba(8,126,164,.13)",
//                                     }}
//                                 >
//                                     <Security
//                                         sx={{
//                                             fontSize: 16,
//                                             color:
//                                                 COLORS.oceanBlue,
//                                         }}
//                                     />

//                                     <Typography
//                                         sx={{
//                                             fontSize: ".62rem",
//                                             fontWeight: 900,
//                                             color:
//                                                 COLORS.deepOcean,
//                                             letterSpacing: 0.6,
//                                             textAlign: "center",
//                                         }}
//                                     >
//                                         SECURE SCHOOL MANAGEMENT PORTAL
//                                     </Typography>
//                                 </Box>

//                                 {/* FORM */}
//                                 <form onSubmit={handleSubmit}>
//                                     <TextField
//                                         fullWidth
//                                         label="Email Address"
//                                         variant="outlined"
//                                         value={email}
//                                         onChange={(e) =>
//                                             setEmail(
//                                                 e.target.value
//                                             )
//                                         }
//                                         required
//                                         autoComplete="email"
//                                         InputProps={{
//                                             startAdornment: (
//                                                 <InputAdornment position="start">
//                                                     <Email
//                                                         sx={{
//                                                             color:
//                                                                 COLORS.oceanBlue,
//                                                         }}
//                                                     />
//                                                 </InputAdornment>
//                                             ),
//                                         }}
//                                         sx={{
//                                             mb: 2,
//                                             "& .MuiOutlinedInput-root": {
//                                                 borderRadius: "14px",
//                                                 background: "#F8FCFE",
//                                                 "& fieldset": {
//                                                     borderColor:
//                                                         COLORS.border,
//                                                 },
//                                                 "&:hover fieldset": {
//                                                     borderColor:
//                                                         "#83CFE7",
//                                                 },
//                                                 "&.Mui-focused fieldset": {
//                                                     borderColor:
//                                                         COLORS.oceanBlue,
//                                                     borderWidth: 2,
//                                                 },
//                                             },
//                                             "& .MuiInputLabel-root.Mui-focused":
//                                                 {
//                                                     color:
//                                                         COLORS.oceanBlue,
//                                                 },
//                                         }}
//                                     />

//                                     <TextField
//                                         fullWidth
//                                         label="Password"
//                                         type={
//                                             showPassword
//                                                 ? "text"
//                                                 : "password"
//                                         }
//                                         variant="outlined"
//                                         value={password}
//                                         onChange={(e) =>
//                                             setPassword(
//                                                 e.target.value
//                                             )
//                                         }
//                                         required
//                                         autoComplete="current-password"
//                                         InputProps={{
//                                             startAdornment: (
//                                                 <InputAdornment position="start">
//                                                     <Lock
//                                                         sx={{
//                                                             color:
//                                                                 COLORS.oceanBlue,
//                                                         }}
//                                                     />
//                                                 </InputAdornment>
//                                             ),
//                                             endAdornment: (
//                                                 <InputAdornment position="end">
//                                                     <IconButton
//                                                         onClick={() =>
//                                                             setShowPassword(
//                                                                 (prev) =>
//                                                                     !prev
//                                                             )
//                                                         }
//                                                         edge="end"
//                                                         aria-label={
//                                                             showPassword
//                                                                 ? "Hide password"
//                                                                 : "Show password"
//                                                         }
//                                                     >
//                                                         {showPassword ? (
//                                                             <VisibilityOff
//                                                                 sx={{
//                                                                     color:
//                                                                         COLORS.muted,
//                                                                 }}
//                                                             />
//                                                         ) : (
//                                                             <Visibility
//                                                                 sx={{
//                                                                     color:
//                                                                         COLORS.muted,
//                                                                 }}
//                                                             />
//                                                         )}
//                                                     </IconButton>
//                                                 </InputAdornment>
//                                             ),
//                                         }}
//                                         sx={{
//                                             "& .MuiOutlinedInput-root": {
//                                                 borderRadius: "14px",
//                                                 background: "#F8FCFE",
//                                                 "& fieldset": {
//                                                     borderColor:
//                                                         COLORS.border,
//                                                 },
//                                                 "&:hover fieldset": {
//                                                     borderColor:
//                                                         "#83CFE7",
//                                                 },
//                                                 "&.Mui-focused fieldset": {
//                                                     borderColor:
//                                                         COLORS.oceanBlue,
//                                                     borderWidth: 2,
//                                                 },
//                                             },
//                                             "& .MuiInputLabel-root.Mui-focused":
//                                                 {
//                                                     color:
//                                                         COLORS.oceanBlue,
//                                                 },
//                                         }}
//                                     />

//                                     <Box
//                                         sx={{
//                                             display: "flex",
//                                             justifyContent:
//                                                 "flex-end",
//                                             mt: 1.3,
//                                             mb: 2.7,
//                                         }}
//                                     >
//                                         <Link
//                                             to="/forgotPassword"
//                                             style={{
//                                                 color:
//                                                     COLORS.deepOrange,
//                                                 fontWeight: 850,
//                                                 textDecoration:
//                                                     "none",
//                                                 fontSize: ".84rem",
//                                             }}
//                                         >
//                                             Forgot Password?
//                                         </Link>
//                                     </Box>

//                                     <Button
//                                         type="submit"
//                                         fullWidth
//                                         disabled={loading}
//                                         variant="contained"
//                                         sx={{
//                                             py: 1.55,
//                                             borderRadius: "14px",
//                                             fontSize: ".97rem",
//                                             fontWeight: 900,
//                                             textTransform: "none",
//                                             color: COLORS.white,
//                                             background:
//                                                 `linear-gradient(135deg, ${COLORS.deepBlue}, ${COLORS.oceanBlue})`,
//                                             boxShadow:
//                                                 "0 14px 30px rgba(4,90,120,.22)",
//                                             transition:
//                                                 "all .25s ease",
//                                             "&:hover": {
//                                                 background:
//                                                     `linear-gradient(135deg, ${COLORS.deepOrange}, ${COLORS.orange})`,
//                                                 boxShadow:
//                                                     "0 16px 35px rgba(249,115,22,.28)",
//                                                 transform:
//                                                     "translateY(-2px)",
//                                             },
//                                             "&:disabled": {
//                                                 background:
//                                                     "#9DB4BE",
//                                             },
//                                         }}
//                                     >
//                                         {loading ? (
//                                             <Box
//                                                 sx={{
//                                                     display: "flex",
//                                                     alignItems:
//                                                         "center",
//                                                     justifyContent:
//                                                         "center",
//                                                     gap: 1.3,
//                                                 }}
//                                             >
//                                                 <CircularProgress
//                                                     size={21}
//                                                     thickness={5}
//                                                     sx={{
//                                                         color:
//                                                             COLORS.white,
//                                                     }}
//                                                 />
//                                                 Signing In...
//                                             </Box>
//                                         ) : (
//                                             <Box
//                                                 sx={{
//                                                     display: "flex",
//                                                     alignItems:
//                                                         "center",
//                                                     justifyContent:
//                                                         "center",
//                                                     gap: 0.9,
//                                                 }}
//                                             >
//                                                 <AutoAwesome
//                                                     sx={{
//                                                         fontSize: 18,
//                                                     }}
//                                                 />
//                                                 Sign In Securely
//                                             </Box>
//                                         )}
//                                     </Button>

//                                     <Button
//                                         fullWidth
//                                         variant="outlined"
//                                         onClick={() =>
//                                             navigate("/")
//                                         }
//                                         startIcon={
//                                             <ArrowBack />
//                                         }
//                                         sx={{
//                                             mt: 1.5,
//                                             py: 1.35,
//                                             borderRadius: "14px",
//                                             fontSize: ".9rem",
//                                             fontWeight: 800,
//                                             textTransform: "none",
//                                             color:
//                                                 COLORS.deepOcean,
//                                             border:
//                                                 `1.5px solid ${COLORS.border}`,
//                                             "&:hover": {
//                                                 background:
//                                                     COLORS.softBlue,
//                                                 borderColor:
//                                                     COLORS.oceanBlue,
//                                                 color:
//                                                     COLORS.deepOcean,
//                                             },
//                                         }}
//                                     >
//                                         Back to Home
//                                     </Button>
//                                 </form>

//                                 {/* FOOTER */}
//                                 <Box
//                                     sx={{
//                                         mt: 3,
//                                         pt: 2.2,
//                                         borderTop:
//                                             `1px solid ${COLORS.border}`,
//                                         textAlign: "center",
//                                     }}
//                                 >
//                                     <Box
//                                         sx={{
//                                             display: "flex",
//                                             alignItems:
//                                                 "center",
//                                             justifyContent:
//                                                 "center",
//                                             gap: 0.7,
//                                             mb: 0.6,
//                                         }}
//                                     >
//                                         <VerifiedUser
//                                             sx={{
//                                                 fontSize: 14,
//                                                 color:
//                                                     COLORS.oceanBlue,
//                                             }}
//                                         />

//                                         <Typography
//                                             sx={{
//                                                 fontSize: ".66rem",
//                                                 fontWeight: 850,
//                                                 color:
//                                                     COLORS.oceanBlue,
//                                                 letterSpacing: 0.5,
//                                             }}
//                                         >
//                                             SECURE AUTHENTICATION
//                                         </Typography>
//                                     </Box>

//                                     <Typography
//                                         sx={{
//                                             fontSize: ".68rem",
//                                             color: "#8AA0AA",
//                                             lineHeight: 1.6,
//                                         }}
//                                     >
//                                         Your credentials are protected
//                                         <br />
//                                         by secure authentication.
//                                     </Typography>
//                                 </Box>
//                             </Paper>
//                         </motion.div>
//                     </Box>
//                 </Box>
//             </Box>
//         </Box>
//     );
// };

// export default Login;